import { on } from "svelte/events";

type PositionType = {
	x: number;
	y: number;
	startX: number;
	startY: number;
};

export class Carousel {
	element: HTMLElement;
	callbackDelta: undefined | ((x: number, y: number, position: PositionType, carouselClass: Carousel) => any);
	callbackEnd: undefined | ((...args: any[]) => any);

  callbackSetup: undefined| Function

	min: undefined | ((...args: any[]) => number | false);
	max: undefined | ((...args: any[]) => number | false);

	wheelTimeout: undefined|any

	id: string;

	propagationAllowed = false;

	left = 0;

	active: boolean = false;

	shouldRollover: boolean = false;

	abort: AbortController | undefined = undefined;

	cleanUp: AbortController | undefined = undefined;

	position: PositionType = {
		x: 0,
		y: 0,
		startX: 0,
		startY: 0
	};

	constructor(element: HTMLElement, callbackDelta?: typeof this.callbackDelta, callbackEnd?: typeof this.callbackEnd, callbackSetup?: Function) {
		this.element = element;
		this.callbackDelta = callbackDelta;
		this.callbackEnd = callbackEnd;
    this.callbackSetup = callbackSetup
		this.id = this.generateID();

		if (this.callbackDelta || this.callbackEnd) this.init();

    if( this.callbackSetup ) this.callbackSetup()
		return this;
	}

	destroy() {
		this.cleanUp?.abort();
		return this;
	}

	init() {
		this.cleanUp = new AbortController();
		on( this.element,'mousedown', this.onStart.bind(this), { signal: this.cleanUp.signal, passive: false, capture: false });
		on( this.element,'touchstart', this.onStart.bind(this), { signal: this.cleanUp.signal, passive: false, capture: false });

		on( this.element,'wheel', this.onWheel.bind(this), { signal: this.cleanUp.signal, passive: false, capture: false });

		if (this.callbackEnd) {
			window.addEventListener(
				'resize',
				() => {
					if (this.callbackEnd) this.callbackEnd(this);
				},
				{ signal: this.cleanUp.signal, passive: false, capture: false }
			);
		}
		return this;
	}

  registerSetupCallback(fn: Function ) {
    this.callbackSetup = fn
    return this
  }

	setMin(_min: (...args: any[]) => number) {
		this.min = _min;
		return this;
	}

	setMax(_max: (...args: any[]) => number) {
		this.max = _max;
		return this;
	}

	setPropagation(allowed = false) {
		this.propagationAllowed = allowed;
		return this;
	}

	preventPropagations(ev: MouseEvent | TouchEvent | WheelEvent) {
		if (ev.type == 'touchstart') return;
		if (ev.type == 'touchmove') return;
		if (this.propagationAllowed) return;
		ev.stopPropagation();
		ev.preventDefault();
	}

	onStart(ev: MouseEvent | TouchEvent | WheelEvent) {
		this.preventPropagations(ev);

		this.setupEventListeners();

		this.active = true;

		if (ev instanceof MouseEvent) {
			this.position.x = ev.clientX;
			this.position.y = ev.clientY;
		}

		if (ev instanceof TouchEvent) {
			this.position.x = ev.touches[0].clientX;
			this.position.y = ev.touches[0].clientY;
		}

		this.position.startX = this.position.x;
		this.position.startY = this.position.y;
	}

	onWheel(ev: WheelEvent) {
		//this.preventPropagations(ev)
		if( ! this.active ) {
			return this.onWheelStart(ev)
		}

		this.position.x = this.position.x + ( -1 * ev.deltaX);
		this.position.y = this.position.y + ( -1 * ev.deltaY);

		if (this.callbackDelta) this.callbackDelta(-1 * ev.deltaX, -1 * ev.deltaY, this.position, this);
	}

	onWheelStart(ev: WheelEvent) {

		if( this.wheelTimeout) {
			clearTimeout(this.wheelTimeout)
		}

		this.active = true

		this.position.startX = 0;
		this.position.startY = 0;

		this.wheelTimeout = setTimeout( () => {
			this.active = false
			this.onEnd(ev)
		} ,500)

		return
	}

	onEnd(ev: MouseEvent | TouchEvent | WheelEvent) {
		this.preventPropagations(ev);

		this.removeEventListeners();

		this.active = false;

		this.position = {
			x: 0,
			y: 0,
			startX: 0,
			startY: 0
		};

		if (this.callbackEnd) this.callbackEnd(this);
	}

	onMove(ev: MouseEvent | TouchEvent | WheelEvent) {
		if (!this.active) return;

		this.preventPropagations(ev);

		let deltaX = 0;
		let deltaY = 0;

		if (this.shouldRollover) this.rollover();

		if (ev instanceof MouseEvent) {
			deltaX = ev.clientX - this.position.x;
			deltaY = ev.clientY - this.position.y;

			this.position.x = ev.clientX;
			this.position.y = ev.clientY;
		}

		if (ev instanceof TouchEvent) {
			deltaX = ev.touches[0].clientX - this.position.x;
			deltaY = ev.touches[0].clientY - this.position.y;

			this.position.x = ev.touches[0].clientX;
			this.position.y = ev.touches[0].clientY;
		}

		//if( Math.abs(this.position.x - this.position.startX) > 200 ) this.shouldRollover = true

		if (this.callbackDelta) this.callbackDelta(deltaX, deltaY, this.position, this);
	}

	rollover() {
		this.position.startX = this.position.x;
		this.position.startY = this.position.y;
		this.shouldRollover = false;
	}

	setupEventListeners() {
		this.abort = new AbortController();
		on( document,'mousemove', this.onMove.bind(this), { signal: this.abort.signal, passive: false });
		on( document,'touchmove', this.onMove.bind(this), { signal: this.abort.signal, passive: false });
		on( document,'mouseup', this.onEnd.bind(this), { signal: this.abort.signal, passive: false });
		on( document,'touchend', this.onEnd.bind(this), { signal: this.abort.signal, passive: false });

		//document.addEventListener('wheel', this.onMove.bind(this), { signal: this.abort.signal, passive: false });
	}

	removeEventListeners() {
		this.abort?.abort();
	}

	getElementOffset(item: HTMLElement) {
		const rect_carousel = this.element.getBoundingClientRect();
		const rect_item = item.getBoundingClientRect();
		const center_carousel = rect_carousel.x + rect_carousel.width / 2;
		const center_item = rect_item.x + rect_item.width / 2;
		const delta = center_carousel - center_item;
		return this.left + (delta / rect_carousel.width) * 100;
	}

	getElementCenter(item: HTMLElement) {
		const rect_item = item.getBoundingClientRect();
		const center_item = rect_item.x + rect_item.width / 2;
		return center_item;
	}

	getElements() {
		const items = [...this.element.querySelectorAll('.' + this.id + ' \> .carousel-item')] as HTMLElement[];
		return items;
	}

	getElementByIndex(index: number) {
		const items = [...this.element.querySelectorAll('.' + this.id + ' \> .carousel-item')] as HTMLElement[];
		if (items.length == 0) return;
		const item = items[index];
		return item;
	}

	getMin() {
		if (this.min) {
			const r = this.min(this);
			if (r !== false) return r;
		}
		const items = [...this.element.querySelectorAll('.' + this.id + ' \> .carousel-item')] as HTMLElement[];
		if (items.length == 0) return;
		const item = items[0];
		return this.getElementOffset(item);
	}

	getMax() {
		if (this.max) {
			const r = this.max(this);
			if (r !== false) return r;
		}
		const items = [...this.element.querySelectorAll('.' + this.id + ' \> .carousel-item')] as HTMLElement[];
		if (items.length == 0) return;
		const item = items[items.length - 1];
		return this.getElementOffset(item);
	}

	getIntersectingElement() {
		const rect_carousel = this.element.getBoundingClientRect();
		const center_carousel = rect_carousel.x + rect_carousel.width / 2;

		const items = [...this.element.querySelectorAll('.' + this.id + ' \> .carousel-item')] as HTMLElement[];
		const centers = items.map((item) => this.getElementCenter(item));
		let closest = undefined;
		let closest_index = 0;
		for (let i = 0; i < centers.length; i++) {
			if (closest == undefined) closest = centers[i];
			else if (Math.abs(centers[i] - center_carousel) < Math.abs(closest - center_carousel)) closest = centers[i];
			if (closest == centers[i]) closest_index = i;
		}

		return closest_index;
	}

	setCarouselDelta(delta: number) {
		const rect_carousel = this.element.getBoundingClientRect();
		let _left = this.left + (delta / rect_carousel.width) * 100;

		let min = this.getMin();
		let max = this.getMax();

		if (min && min < _left) _left = min;
		else if (max && max > _left) _left = max;

		//this.left = _left;
		return _left;
	}

	getID() {
		return this.id;
	}

	generateID() {
		let chars = 'abcdefghijklmnopqrstuvwxz';
		chars += chars.toUpperCase();

		let string = '';
		for (let i = 0; i < 7; i++) {
			string += chars[this.randomBetween(0, chars.length - 1)];
		}

		return string;
	}

	randomBetween(min: number, max: number) {
		const seed = Math.random();
		return Math.floor((max - min + 1) * seed + min);
	}
}
